import { Fragment, useState } from 'react'
import { FiX, FiPlus, FiTrash2 } from 'react-icons/fi'
import { toast } from 'react-toastify'
import {
  LibraryOverlay,
  LibraryModal,
  LibraryHeader,
  LibraryTabs,
  LibraryTabButton,
  LibraryBody,
  LibraryTable,
  LibraryTh,
  LibraryTd,
  LibraryDetailRow,
  LibraryDetailCell,
  LibraryFootRow,
  SmallButton,
  RemoveButton,
  MovingLoadsInfoNote,
} from './canvasStyles'
import {
  createMaterial,
  createSection,
  createLoadCase,
  createCombination,
  computeSectionProperties,
  cascadeRemoveMaterial,
  cascadeRemoveLoadCase,
  cascadeRenameMaterial,
  cascadeRenameSection,
  cascadeRenameLoadCase,
} from './model'
import { useOverlayClose } from './useOverlayClose'

function ParamField({ label, value, onChange }) {
  return (
    <label>
      {label}
      <input type="number" value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  )
}

function shapeParamFields(section, onChange) {
  if (section.shape === 'rectangular') {
    return (
      <>
        <ParamField label="b (cm)" value={section.b} onChange={(v) => onChange('b', v)} />
        <ParamField label="h (cm)" value={section.h} onChange={(v) => onChange('h', v)} />
      </>
    )
  }
  if (section.shape === 'tSection' || section.shape === 'invertedTSection' || section.shape === 'iSection') {
    const isI = section.shape === 'iSection'
    const isInvertedT = section.shape === 'invertedTSection'
    return (
      <>
        <ParamField label="bf (cm) — largura da mesa" value={section.bf} onChange={(v) => onChange('bf', v)} />
        <ParamField
          label={isI ? 'tf (cm) — espessura da mesa' : `hf (cm) — altura da mesa${isInvertedT ? ' (inferior)' : ''}`}
          value={section.hf}
          onChange={(v) => onChange('hf', v)}
        />
        <ParamField
          label={isI ? 'tw (cm) — espessura da alma' : 'bw (cm) — largura da alma'}
          value={section.bw}
          onChange={(v) => onChange('bw', v)}
        />
        <ParamField label="h (cm) — altura total" value={section.h} onChange={(v) => onChange('h', v)} />
      </>
    )
  }
  if (section.shape === 'generic') {
    return (
      <>
        <ParamField label="A (m²)" value={section.customA} onChange={(v) => onChange('customA', v)} />
        <ParamField label="I (m⁴)" value={section.customI} onChange={(v) => onChange('customI', v)} />
      </>
    )
  }
  return null
}

// Renders the geometry inputs relevant to the section's shape, in the wider LibraryDetailRow below
// the section's main row (see the sections tab) rather than squeezed into a table column. T and I
// sections share the same fields (bf/hf = flange width/thickness, bw = web width/thickness, h =
// total height) — see computeSectionProperties in model.js — only the labels differ between them.
// When the section's material is concrete, also asks for the distance from each rebar layer to its
// nearest face (cover + half the bar diameter, in effect) — meaningless for a steel section.
function SectionParamsFields({ section, materials, onChange }) {
  const material = materials.find((m) => m.id === section.materialId)
  const isConcrete = material?.materialType === 'concrete'
  return (
    <>
      {shapeParamFields(section, onChange)}
      {isConcrete && (
        <>
          <ParamField
            label="d inf. armadura → face (cm)"
            value={section.rebarDistanceBottom}
            onChange={(v) => onChange('rebarDistanceBottom', v)}
          />
          <ParamField
            label="d sup. armadura → face (cm)"
            value={section.rebarDistanceTop}
            onChange={(v) => onChange('rebarDistanceTop', v)}
          />
        </>
      )}
    </>
  )
}

export function LibraryPanel({
  materials,
  sections,
  loadCases,
  combinations,
  elements,
  trains = [],
  nodalLoads,
  elementLoads,
  setMaterials,
  setSections,
  setLoadCases,
  setCombinations,
  setElements,
  setNodalLoads,
  setElementLoads,
  onClose,
}) {
  const [tab, setTab] = useState('materials')

  function addMaterial() {
    setMaterials((prev) => [...prev, createMaterial(prev)])
  }
  // Renaming a material's id must follow into every section referencing it via materialId, or the
  // rename silently drops the reference even though the material itself didn't actually change.
  function updateMaterial(key, field, value) {
    if (field === 'id') {
      const material = materials.find((m) => m.key === key)
      if (material && material.id !== value) {
        setSections(cascadeRenameMaterial(material.id, value, { sections }).sections)
      }
    }
    setMaterials((prev) => prev.map((m) => (m.key === key ? { ...m, [field]: value } : m)))
  }
  // Suggests E (Ecs, the secant modulus) from fck per NBR 6118, assuming alpha_E = 1
  // (granito/gnaisse) — same formula used for the alpha_e suggestion in the Inércia Equivalente
  // calculator (see BransonInertia/index.js), just returning Ecs itself instead of Es/Ecs.
  function suggestConcreteE(fckValue) {
    const fckNum = Number(fckValue)
    if (!fckNum || fckNum <= 0) return null
    const eci = fckNum <= 50 ? 5600 * Math.sqrt(fckNum) : 21500 * (fckNum / 10 + 1.25) ** (1 / 3)
    const alphaI = Math.min(0.8 + 0.2 * (fckNum / 80), 1)
    const ecsMPa = alphaI * eci
    if (!ecsMPa) return null
    return ecsMPa * 1000 // MPa -> kN/m²
  }

  function handleFckBlur(key, fckValue) {
    const suggested = suggestConcreteE(fckValue)
    if (suggested !== null) {
      updateMaterial(key, 'E', Number(suggested.toFixed(0)))
      toast.info(
        'E sugerido a partir do fck (NBR 6118), considerando α_E = 1 (agregado graúdo granito ou gnaisse). Ajuste manualmente se o agregado for outro.',
        { autoClose: 9000 }
      )
    }
  }

  function removeMaterial(key) {
    const result = cascadeRemoveMaterial(key, { materials, sections })
    setMaterials(result.materials)
    setSections(result.sections)
  }

  function addSection() {
    setSections((prev) => [...prev, createSection(prev, materials[0]?.id || '')])
  }
  // Renaming a section's id must follow into every element's sectionId and every combination's
  // elementOverrides (which stores a sectionId as the override value), same reasoning as above.
  function updateSection(key, field, value) {
    if (field === 'id') {
      const section = sections.find((s) => s.key === key)
      if (section && section.id !== value) {
        const result = cascadeRenameSection(section.id, value, { elements, combinations })
        setElements(result.elements)
        setCombinations(result.combinations)
      }
    }
    setSections((prev) => prev.map((s) => (s.key === key ? { ...s, [field]: value } : s)))
  }
  // Elements referencing a removed section are not touched here — index.js's validation already
  // flags any element whose sectionId no longer resolves to a real section.
  function removeSection(key) {
    setSections((prev) => prev.filter((s) => s.key !== key))
  }

  function addLoadCase() {
    setLoadCases((prev) => [...prev, createLoadCase(prev)])
  }
  // Renaming a load case's id must follow into every nodal/element load's loadCase and every
  // combination's factors key, or the rename silently drops the reference even though the case's
  // actual loads/factor didn't change.
  function updateLoadCase(key, field, value) {
    if (field === 'id') {
      const loadCase = loadCases.find((c) => c.key === key)
      if (loadCase && loadCase.id !== value) {
        const result = cascadeRenameLoadCase(loadCase.id, value, { nodalLoads, elementLoads, combinations })
        setNodalLoads(result.nodalLoads)
        setElementLoads(result.elementLoads)
        setCombinations(result.combinations)
      }
    }
    setLoadCases((prev) => prev.map((c) => (c.key === key ? { ...c, [field]: value } : c)))
  }
  function removeLoadCase(key) {
    const result = cascadeRemoveLoadCase(key, { loadCases, combinations, nodalLoads, elementLoads })
    setLoadCases(result.loadCases)
    setCombinations(result.combinations)
    setNodalLoads(result.nodalLoads)
    setElementLoads(result.elementLoads)
  }

  function addCombination() {
    setCombinations((prev) => [...prev, createCombination(prev)])
  }
  function updateCombination(key, field, value) {
    setCombinations((prev) => prev.map((c) => (c.key === key ? { ...c, [field]: value } : c)))
  }
  function removeCombination(key) {
    setCombinations((prev) => prev.filter((c) => c.key !== key))
  }
  function updateFactor(key, caseId, value) {
    setCombinations((prev) =>
      prev.map((c) => {
        if (c.key !== key) return c
        const factors = { ...c.factors }
        if (value === '') {
          delete factors[caseId]
        } else {
          factors[caseId] = value
        }
        return { ...c, factors }
      })
    )
  }
  // A train factor turns a combination into a moving-load combination: it's resolved per train
  // position by the moving-loads endpoint and shown under that train's view.
  function updateTrainFactor(key, trainId, value) {
    setCombinations((prev) =>
      prev.map((c) => {
        if (c.key !== key) return c
        const trainFactors = { ...(c.trainFactors || {}) }
        if (value === '') {
          delete trainFactors[trainId]
        } else {
          trainFactors[trainId] = value
        }
        return { ...c, trainFactors }
      })
    )
  }
  const overlayCloseHandlers = useOverlayClose(onClose)
  return (
    <LibraryOverlay {...overlayCloseHandlers}>
      <LibraryModal onClick={(e) => e.stopPropagation()}>
        <LibraryHeader>
          <LibraryTabs>
            <LibraryTabButton type="button" $active={tab === 'materials'} onClick={() => setTab('materials')}>
              Materiais
            </LibraryTabButton>
            <LibraryTabButton type="button" $active={tab === 'sections'} onClick={() => setTab('sections')}>
              Seções
            </LibraryTabButton>
            <LibraryTabButton type="button" $active={tab === 'loadCases'} onClick={() => setTab('loadCases')}>
              Casos de Carga
            </LibraryTabButton>
            <LibraryTabButton type="button" $active={tab === 'combinations'} onClick={() => setTab('combinations')}>
              Combinações
            </LibraryTabButton>
          </LibraryTabs>
          <button type="button" onClick={onClose} title="Fechar">
            <FiX />
          </button>
        </LibraryHeader>

        <LibraryBody>
          {tab === 'materials' && (
            <>
              <LibraryTable>
                <thead>
                  <tr>
                    <LibraryTh>Id</LibraryTh>
                    <LibraryTh>Nome</LibraryTh>
                    <LibraryTh>Tipo</LibraryTh>
                    <LibraryTh>fck (MPa)</LibraryTh>
                    <LibraryTh>E (módulo de elasticidade) (kN/m²)</LibraryTh>
                    <LibraryTh>Peso específico (kN/m³)</LibraryTh>
                    <LibraryTh></LibraryTh>
                  </tr>
                </thead>
                <tbody>
                  {materials.map((m) => (
                    <tr key={m.key}>
                      <LibraryTd>
                        <input value={m.id} onChange={(e) => updateMaterial(m.key, 'id', e.target.value)} />
                      </LibraryTd>
                      <LibraryTd>
                        <input
                          value={m.name}
                          placeholder="ex.: Concreto C25"
                          onChange={(e) => updateMaterial(m.key, 'name', e.target.value)}
                        />
                      </LibraryTd>
                      <LibraryTd>
                        <select
                          value={m.materialType || 'concrete'}
                          onChange={(e) => updateMaterial(m.key, 'materialType', e.target.value)}
                        >
                          <option value="concrete">Concreto</option>
                          <option value="steel">Aço Laminado</option>
                        </select>
                      </LibraryTd>
                      <LibraryTd>
                        {m.materialType === 'concrete' && (
                          <input
                            type="number"
                            value={m.fck ?? ''}
                            onChange={(e) => updateMaterial(m.key, 'fck', e.target.value)}
                            onBlur={(e) => handleFckBlur(m.key, e.target.value)}
                          />
                        )}
                      </LibraryTd>
                      <LibraryTd>
                        <input type="number" value={m.E} onChange={(e) => updateMaterial(m.key, 'E', e.target.value)} />
                      </LibraryTd>
                      <LibraryTd>
                        <input
                          type="number"
                          value={m.specificWeight ?? ''}
                          placeholder="opcional"
                          onChange={(e) => updateMaterial(m.key, 'specificWeight', e.target.value)}
                        />
                      </LibraryTd>
                      <LibraryTd>
                        <RemoveButton type="button" onClick={() => removeMaterial(m.key)}>
                          <FiTrash2 />
                        </RemoveButton>
                      </LibraryTd>
                    </tr>
                  ))}
                </tbody>
              </LibraryTable>
              <LibraryFootRow>
                <SmallButton type="button" onClick={addMaterial}>
                  <FiPlus /> Adicionar material
                </SmallButton>
              </LibraryFootRow>
            </>
          )}

          {tab === 'sections' && (
            <>
              <LibraryTable>
                <thead>
                  <tr>
                    <LibraryTh>Id</LibraryTh>
                    <LibraryTh>Nome</LibraryTh>
                    <LibraryTh>Geometria</LibraryTh>
                    <LibraryTh>Material</LibraryTh>
                    <LibraryTh>A (m²)</LibraryTh>
                    <LibraryTh>I (m⁴)</LibraryTh>
                    <LibraryTh></LibraryTh>
                  </tr>
                </thead>
                <tbody>
                  {sections.map((s) => {
                    const { A, I } = computeSectionProperties(s)
                    return (
                      <Fragment key={s.key}>
                        <tr>
                          <LibraryTd $noBottomBorder>
                            <input value={s.id} onChange={(e) => updateSection(s.key, 'id', e.target.value)} />
                          </LibraryTd>
                          <LibraryTd $noBottomBorder>
                            <input
                              value={s.name}
                              placeholder="ex.: Viga 20x40"
                              onChange={(e) => updateSection(s.key, 'name', e.target.value)}
                            />
                          </LibraryTd>
                          <LibraryTd $noBottomBorder>
                            <select value={s.shape} onChange={(e) => updateSection(s.key, 'shape', e.target.value)}>
                              <option value="rectangular">Retangular</option>
                              <option value="tSection">T</option>
                              <option value="invertedTSection">T invertido</option>
                              <option value="iSection">I</option>
                              <option value="generic">Genérica</option>
                            </select>
                          </LibraryTd>
                          <LibraryTd $noBottomBorder>
                            <select
                              value={s.materialId}
                              onChange={(e) => updateSection(s.key, 'materialId', e.target.value)}
                            >
                              <option value="">-</option>
                              {materials.map((m) => (
                                <option key={m.key} value={m.id}>
                                  {m.id}
                                </option>
                              ))}
                            </select>
                          </LibraryTd>
                          <LibraryTd $noBottomBorder>{A.toFixed(5)}</LibraryTd>
                          <LibraryTd $noBottomBorder>{I.toFixed(7)}</LibraryTd>
                          <LibraryTd $noBottomBorder>
                            <RemoveButton type="button" onClick={() => removeSection(s.key)}>
                              <FiTrash2 />
                            </RemoveButton>
                          </LibraryTd>
                        </tr>
                        <LibraryDetailRow>
                          <LibraryDetailCell colSpan={7}>
                            <SectionParamsFields
                              section={s}
                              materials={materials}
                              onChange={(field, value) => updateSection(s.key, field, value)}
                            />
                          </LibraryDetailCell>
                        </LibraryDetailRow>
                      </Fragment>
                    )
                  })}
                </tbody>
              </LibraryTable>
              <LibraryFootRow>
                <SmallButton type="button" onClick={addSection}>
                  <FiPlus /> Adicionar seção
                </SmallButton>
              </LibraryFootRow>
            </>
          )}

          {tab === 'loadCases' && (
            <>
              <LibraryTable>
                <thead>
                  <tr>
                    <LibraryTh>Id</LibraryTh>
                    <LibraryTh>Nome</LibraryTh>
                    <LibraryTh>Categoria</LibraryTh>
                    <LibraryTh>Peso próprio</LibraryTh>
                    <LibraryTh></LibraryTh>
                  </tr>
                </thead>
                <tbody>
                  {loadCases.map((c) => (
                    <tr key={c.key}>
                      <LibraryTd>
                        <input value={c.id} onChange={(e) => updateLoadCase(c.key, 'id', e.target.value)} />
                      </LibraryTd>
                      <LibraryTd>
                        <input
                          value={c.name}
                          placeholder="ex.: Permanente"
                          onChange={(e) => updateLoadCase(c.key, 'name', e.target.value)}
                        />
                      </LibraryTd>
                      <LibraryTd>
                        <input
                          value={c.category}
                          placeholder="ex.: permanent, variable, wind"
                          onChange={(e) => updateLoadCase(c.key, 'category', e.target.value)}
                        />
                      </LibraryTd>
                      <LibraryTd>
                        <input
                          type="checkbox"
                          checked={!!c.selfWeight}
                          title="Gera automaticamente a carga distribuída de peso próprio dos elementos"
                          onChange={(e) => updateLoadCase(c.key, 'selfWeight', e.target.checked)}
                        />
                      </LibraryTd>
                      <LibraryTd>
                        <RemoveButton type="button" onClick={() => removeLoadCase(c.key)}>
                          <FiTrash2 />
                        </RemoveButton>
                      </LibraryTd>
                    </tr>
                  ))}
                </tbody>
              </LibraryTable>
              <LibraryFootRow>
                <SmallButton type="button" onClick={addLoadCase}>
                  <FiPlus /> Adicionar caso de carga
                </SmallButton>
              </LibraryFootRow>
            </>
          )}

          {tab === 'combinations' && (
            <>
              <LibraryTable>
                <thead>
                  <tr>
                    <LibraryTh>Id</LibraryTh>
                    <LibraryTh>Nome</LibraryTh>
                    <LibraryTh>Tipo</LibraryTh>
                    <LibraryTh></LibraryTh>
                  </tr>
                </thead>
                <tbody>
                  {combinations.map((c) => (
                    <tr key={c.key}>
                      <LibraryTd>
                        <input value={c.id} onChange={(e) => updateCombination(c.key, 'id', e.target.value)} />
                      </LibraryTd>
                      <LibraryTd>
                        <input
                          value={c.name}
                          placeholder="ex.: ELU"
                          onChange={(e) => updateCombination(c.key, 'name', e.target.value)}
                        />
                      </LibraryTd>
                      <LibraryTd>
                        <select value={c.kind} onChange={(e) => updateCombination(c.key, 'kind', e.target.value)}>
                          <option value="ultimate">Último (ELU)</option>
                          <option value="service">Serviço (ELS)</option>
                        </select>
                      </LibraryTd>
                      <LibraryTd>
                        <RemoveButton type="button" onClick={() => removeCombination(c.key)}>
                          <FiTrash2 />
                        </RemoveButton>
                      </LibraryTd>
                    </tr>
                  ))}
                </tbody>
              </LibraryTable>
              <LibraryFootRow>
                <SmallButton type="button" onClick={addCombination}>
                  <FiPlus /> Adicionar combinação
                </SmallButton>
              </LibraryFootRow>

              {combinations.length > 0 && (loadCases.length > 0 || trains.length > 0) && (
                <>
                  <h4 style={{ marginTop: '20px', marginBottom: '8px' }}>Fatores por caso e trem-tipo</h4>
                  <MovingLoadsInfoNote>
                    Preencha um fator na coluna de um trem para transformar a combinação em uma combinação de carga
                    móvel — ela é resolvida por posição do trem e aparece na vista daquele trem.
                  </MovingLoadsInfoNote>
                  <LibraryTable>
                    <thead>
                      <tr>
                        <LibraryTh>Combinação</LibraryTh>
                        {loadCases.map((lc) => (
                          <LibraryTh key={lc.key}>{lc.id}</LibraryTh>
                        ))}
                        {trains.map((t) => (
                          <LibraryTh key={t.key}>Trem {t.id}</LibraryTh>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {combinations.map((c) => (
                        <tr key={c.key}>
                          <LibraryTd>{c.id}</LibraryTd>
                          {loadCases.map((lc) => (
                            <LibraryTd key={lc.key}>
                              <input
                                type="number"
                                placeholder="-"
                                value={c.factors?.[lc.id] ?? ''}
                                onChange={(e) => updateFactor(c.key, lc.id, e.target.value)}
                              />
                            </LibraryTd>
                          ))}
                          {trains.map((t) => (
                            <LibraryTd key={t.key}>
                              <input
                                type="number"
                                placeholder="-"
                                value={c.trainFactors?.[t.id] ?? ''}
                                onChange={(e) => updateTrainFactor(c.key, t.id, e.target.value)}
                              />
                            </LibraryTd>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </LibraryTable>
                </>
              )}
            </>
          )}
        </LibraryBody>
      </LibraryModal>
    </LibraryOverlay>
  )
}
